<template>
  <div class="h-full flex flex-col">
    <ChatHeader title="Conversation History" :editable="false" @close="handleClose" />

    <div class="p-4 border-b border-gpx_gray-900">
      <div class="relative">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg class="h-5 w-5 text-gpx_gray-700" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
          </svg>
        </div>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search..."
          class="form-input w-full rounded-md border border-gpx_gray-800 py-2 pl-10 pr-4 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
          @input="handleSearchInput"
        />
      </div>
    </div>

    <div class="flex-grow overflow-y-auto">
      <div v-if="isLoading" class="flex items-center justify-center h-full">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gpx_slate-100"></div>
      </div>
      <div v-else>
        <div
          v-for="dialog in dialogs"
          :key="dialog.id"
          class="border-b border-gpx_gray-900"
        >
          <div 
            class="w-full p-4 hover:bg-gpx_gray-900 transition-colors cursor-pointer"
            @click="selectDialog(dialog.id)"
          >
            <div class="flex justify-between items-start mb-1">
              <div class="flex items-center">
                <h3 class="font-medium text-gpx_black-500">
                  {{ dialog.title || 'Untitled Conversation' }}
                </h3>
              </div>
              <DialogContextMenu
                :dialog="dialog"
                @action-completed="refetch"
                @click.stop
              />
            </div>
            <div class="flex items-center">
              <svg class="w-3 h-3 text-gpx_gray-700 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"></path>
              </svg>
              <p class="text-xs text-gpx_gray-700">{{ formatRelativeTime(dialog.created_at) }}</p>
            </div>
          </div>
        </div>
        <div v-if="dialogs && dialogs.length === 0" class="p-4 text-center text-gpx_gray-700">
          <p>Nothing...</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import ChatHeader from './ChatHeader.vue';
import { useStore } from 'vuex';
import { getAccountDialogs } from '@gpx-spa/queries';
import { debounce } from 'lodash';
import { formatDistanceToNow, parseISO } from 'date-fns';
import DialogContextMenu from './DialogContextMenu.vue';
import * as event from "@/events";

const store = useStore();
const accountId = store.getters['auth/accountId'];
const searchQuery = ref('');

const { data: dialogs, isLoading, refetch } = getAccountDialogs(accountId, searchQuery);

const emit = defineEmits(['dialogSelected', 'close']);

const handleSearchInput = debounce(() => {
  event.trigger(event.names.SCOUT_SEARCH_USED);
  refetch()
}, 400);

const formatRelativeTime = (dateString: string): string => {
  const date = parseISO(dateString);
  return formatDistanceToNow(date, { addSuffix: true });
};

const selectDialog = (id: number) => {
  emit('dialogSelected', id);
};

const handleClose = () => {
  emit('close');
};
</script>
